import ExpenseComponent from '../review-expense/expense-component/expense-component.jsx';
import { useState, useEffect, useCallback, useRef } from 'react';
import MainInfo from '../review-expense/main-info/main-info.jsx';
import { useParams } from 'react-router-dom';
import { inject } from 'mobx-react';
import withRouter from '../../components/withRouter.js';
import useLanguageChange from '../../utils/hooks/useLanguageChange.js';
import { fetch } from '../../utils/api-wrapper.js';
import PDFVisualizer from '../../components/pdf-visualizer.jsx';
import { downloadFile } from '../../utils/file-manager.js';
import { buildAttachmentsListUrl, resolveAttachmentsCount } from '../../utils/attachments.js';
import './index.scss';
import ReceiptSolid from '../../assets/img/receipt-solid.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { validateExpenseReviewForm } from '../review-expense/utils/validate-form.js';
import Modal from '../../components/modal.js';
import { getCachedInvoiceImageUrl } from '../../utils/invoice-image-cache';

const defaultExpense = {
  matter: '',
  date: '',
  type: '',
  activityCode: '',
  info: {
    supplier: '',
    narrative: '',
    amount: 0,
    numberOfGuests: 0,
    mileage: {
      distance: 0,
      rate: 0.6,
    },
  },
};

const ReviewTransaction = ({ data, navigate, params }) => {
  useLanguageChange(data.t);
  const { statement_id, transaction_checksum } = useParams();
  const [transactionData, setTransactionData] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [pdfBuf, setPdfBuf] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isTransactionValid, setIsTransactionValid] = useState(false);
  const [showConfirmGoBack, setShowConfirmGoBack] = useState(false);
  const [payees, setPayees] = useState([]);
  // XPEN-3127 — paperclip badge seed (same pattern as transaction.js / XPEN-3009).
  const [attachmentsCount, setAttachmentsCount] = useState(0);
  const attachmentsRequestGenerationRef = useRef(0);
  const [pdfView, setPdfView] = useState('invoice');

  const handleTransactionValidationChange = useCallback((isValid) => {
    setIsTransactionValid(isValid);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const errorCallback = (error) => {
          //console.error('API Error:', error);
          if (error.response) {
            //console.error('Response data:', error.response.data);
            //console.error('Response status:', error.response.status);
          }
        };

        const getInvoicePreview = (transaction) => {
          const fileName = transaction?.json?.Name || `${statement_id}_${transaction_checksum}.pdf` || transaction?.file_name;
          if (!fileName) return;
          getCachedInvoiceImageUrl(fileName)
            .then((url) => {
              if (url) {
                setPdfBuf(url);
              }
            })
            .catch(() => {
              setPdfBuf(null);
              setErrorMessages([data.t.read('review_expense.error.fetch_invoice_pdf_failed')]);
            });
        };

        const successCallback = (response) => {
          if (response && response.data) {
            const { statement } = response.data;
            if (statement) {
              setStatementData(statement);
              const transaction = statement.transactions.find(t => t.checksum === transaction_checksum);
              setTransactionData(transaction);
              getInvoicePreview(transaction);
            } else {
              //console.error('Statement data is null or undefined in the response');
            }
          } else {
            //console.error('Response or response.data is missing');
          }
        };
        

        await fetch(`/c_card/statement/?statement_id=${statement_id}`, 'GET', {}, successCallback, errorCallback);

        const queryParams = new URLSearchParams({
          start: 0,
          page_size: 100,
          sorting: JSON.stringify([]),
          filters: JSON.stringify([]),
        }).toString();

        fetch(
          `/users/?${queryParams}`,
          'GET',
          {},
          (response) => {
            setPayees(response.data.users);
          },
          errorCallback
        );
      } catch (error) {
        //console.error('Unexpected error in fetchData:', error);
      }
    };

    if (statement_id) {
      fetchData();
    } else {
      //console.warn('No account_id provided in params');
    }
  }, [statement_id]);

  // XPEN-3127 — seed the paperclip badge for this receipt. Gate on both route
  // ids (list without owner_sub_id is statement-wide) and on showPdf — this
  // page only mounts PDFVisualizer inside the View Receipt dialog, so do not
  // hit GET /attachments until the viewer is actually shown.
  useEffect(() => {
    attachmentsRequestGenerationRef.current += 1;
    const requestedGeneration = attachmentsRequestGenerationRef.current;
    setAttachmentsCount(0);

    if (!showPdf || !statement_id || !transaction_checksum) {
      return;
    }

    fetch(
      buildAttachmentsListUrl('cc_receipt', statement_id, transaction_checksum),
      'GET',
      {},
      (response) => {
        if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
        setAttachmentsCount(resolveAttachmentsCount(response));
      },
      () => {
        if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
        setAttachmentsCount(0);
      }
    );
  }, [statement_id, transaction_checksum, showPdf]);

  // Leave the attachments tab when the route identity changes.
  useEffect(() => {
    setPdfView('invoice');
  }, [statement_id, transaction_checksum]);

  // Reopen the dialog on the document tab, not the paperclip.
  useEffect(() => {
    if (!showPdf) {
      setPdfView('invoice');
    }
  }, [showPdf]);

  const [mainInfo, setMainInfo] = useState({
    invoiceNumber: '',
    payee: undefined,
    date: new Date().toISOString().slice(0, 10),
  });

  const [transaction, setTransaction] = useState(defaultExpense);

  useEffect(() => {
    if (transactionData) {
      const toISODate = (input) => {
        if (!input) return new Date().toISOString().slice(0, 10);
        const d = new Date(input);
        return isNaN(d.getTime()) ? new Date().toISOString().slice(0, 10) : d.toISOString().slice(0, 10);
      };

      setMainInfo((prevInfo) => ({
        ...prevInfo,
        invoiceNumber: transactionData?.json?.InvoiceNo || '',
        date: toISODate(transactionData?.json?.InvoiceDate ?? statementData?.created_at?.$date),
        payee: transactionData?.json?.Payee || statementData?.user || undefined,
      }));
      setupTransactionData();
    }
  }, [transactionData]);

  useEffect(() => {
    if (transactionData && transactionData.json) {
      setIsTransactionValid(Boolean(
        transaction.matter && 
        transaction.date && 
        transaction.type && 
        transaction.info && 
        transaction.info.supplier && 
        transaction.info.amount
      ));
    }
  }, [transactionData, transaction]);

  const setupTransactionData = () => {
    if (transactionData?.json) {
      const customExpenseData = {
        matter: transactionData.json.Matter || '',
        date: transactionData.json.InvoiceDate || new Date().toISOString().slice(0, 10),
        type: transactionData.json.ExpenseType || '',
        activityCode: transactionData.json.ActivityCode || '',
        info: {
          supplier: transactionData.supplier || transactionData.transaction_name,
          narrative: transactionData.json.Description || transactionData.json.Category,
          amount: transactionData.json.Total ? Number(parseFloat(transactionData.json.Total).toFixed(2)) : 0,
          numberOfGuests: transactionData.json.NumberOfGuests || 0,
          mileage: {
            distance: transactionData.json.MileageDistance || 0,
            rate: transactionData.json.MileageRate ? Number(parseFloat(transactionData.json.MileageRate).toFixed(2)) : 0.6,
          },
        },
      };
      setTransaction(customExpenseData);
    } else {
      //console.warn('invoiceData or invoiceData.json is not available');
    }
  };

  const handleUpdateTransaction = (info) => {
    setTransaction({
      ...transaction,
      ...info,
    });
  };

  useEffect(() => {
    if (showPdf && pdfBuf) {
      const dialogElement = document.querySelector('dialog.invoice');
      if (dialogElement) {
        dialogElement.showModal();

        const handleClickOutside = (event) => {
          const dialogDimensions = dialogElement.getBoundingClientRect();
          if (
            event.clientX < dialogDimensions.left ||
            event.clientX > dialogDimensions.right ||
            event.clientY < dialogDimensions.top ||
            event.clientY > dialogDimensions.bottom
          ) {
            setShowPdf(false);
          }
        };

        dialogElement.addEventListener('click', handleClickOutside);

        return () => {
          dialogElement.removeEventListener('click', handleClickOutside);
        };
      }
    } else {
      const dialogElement = document.querySelector('dialog.invoice');
      if (dialogElement && dialogElement.open) {
        dialogElement.close();
      }
    }
  }, [showPdf, pdfBuf]);

  const validateForm = () => {
    const errors = validateExpenseReviewForm(data, mainInfo, [transaction]);
    if (errors.length > 0) {
      setErrorMessages(errors);
      return false;
    }

    setErrorMessages([]);
    return true;
  };

  const submitForm = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const user = {
        id_user: mainInfo.payee._id?.$oid || mainInfo.payee.id_user,
        first_name: mainInfo.payee.first_name,
        last_name: mainInfo.payee.last_name,
        username: mainInfo.payee.email || mainInfo.payee.username,
      };
      const payload = {
        ...transactionData,
      };

      payload.json = {
        ...payload.json,
        Payee: user || transactionData.by_user,
        Matter: transaction.matter,
        InvoiceNo: mainInfo.invoiceNumber,
        InvoiceDate: mainInfo.date,
        ExpenseType: transaction.type,
        ActivityCode: transaction.activityCode,
        supplier: transaction.info.supplier,
        NumberOfGuests: transaction.info.numberOfGuests || 0,
        MileageDistance: transaction.info.mileage?.distance || 0,
        MileageRate: transaction.info.mileage?.rate || 0,
        MileageAmount: (transaction.info.mileage?.distance || 0) * (transaction.info.mileage?.rate || 0),
        Description: transaction.info.narrative,
        Total: parseFloat(transaction.info.amount),
        Reviewed: true,
      };

      fetch(
        `/c_card/transaction/${statement_id}/${transactionData.transaction_id}`,
        'PUT',
        payload,
        (response) => {
          if (response && response.data) {
            navigate('/c_card/statement/' + statement_id);
          } else {
            //console.error('Unexpected response format:', response);
          }
        },
        (error) => {
          //console.error('Error updating invoice:', error);
          setErrorMessages([data.t.read('review_expense.error.update_failed')]);
        }
      );
    }
  };

  // PDFVisualizer shows the paperclip whenever ownerType+ownerId are set —
  // without a real checksum that would list statement-wide attachments.
  // Gate all three owner props together.
  const attachmentsOwnerReady = Boolean(statement_id && transaction_checksum);

  return (
    <>
      {showPdf && pdfBuf && (
        <dialog className='invoice'>
          <PDFVisualizer
            pdfBuff={pdfBuf}
            data={data}
            downloadFile={downloadFile}
            // statement_id alone is shared by every receipt on the statement —
            // checksum identifies this document (same pairing as the PDF path).
            id={`${statement_id}_${transaction_checksum}`}
            showInvoiceExports={false}
            primaryTabLabel={data.t.read('document_tab_label')}
            filenamePrefix="cc_receipt"
            ownerType={attachmentsOwnerReady ? 'cc_receipt' : null}
            ownerId={attachmentsOwnerReady ? statement_id : null}
            ownerSubId={attachmentsOwnerReady ? transaction_checksum : null}
            attachmentsCount={attachmentsCount}
            pdfView={pdfView}
            changePdfView={setPdfView}
          />
        </dialog>
      )}
      {showConfirmGoBack && (
        <Modal
          title={data.t.read('confirm')}
          cancelAction={() => setShowConfirmGoBack(false)}
          okAction={() => navigate('/c_card/statement/' + statement_id)}
          okText={data.t.read('yes')}
          cancelText={data.t.read('cancel')}
        >
          {data.t.read('go_back_conrifmation')}
        </Modal>
      )}
      <form className='card p-5 my-5 rounded main-container' noValidate onSubmit={(e) => e.preventDefault()}>
        <button className='btn statement-container' onClick={() => setShowPdf(!showPdf)}>
          {data.t.read('review_expense.view_receipt')}
          <img src={ReceiptSolid} className='statement' alt='Receipt' />
        </button>

        <h1 className='mb-4'>{data.t.read('review_expense.title')}</h1>
        <button className='btn btn-light mb-3 col-1' onClick={() => setShowConfirmGoBack(true)} type='button'>
          <FontAwesomeIcon icon={faArrowLeft} className='mr-2' />
          {data.t.read('back')}
        </button>
        <MainInfo payees={payees} mainInfo={mainInfo} setMainInfo={setMainInfo} />
        <ExpenseComponent
          expenseIndex={0}
          expense={transaction}
          updateInfo={(info) => handleUpdateTransaction(info)}
          onValidationChange={(_, isValid) => handleTransactionValidationChange(isValid)}
          imputationReviewForUserId={null}
          data-testid="expense-component"
        />
        <div className='error-messages'>
          {errorMessages.length > 0 && (
            <div className='alert alert-danger'>
              <ul>
                {errorMessages.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className='d-flex justify-content-end'>
          <button
            disabled={!isTransactionValid}
            className='btn btn-primary mt-3 col-3'
            onClick={(e) => submitForm(e)}
          >
            {data.t.read('review_expense.save_modifications')}
          </button>
        </div>
      </form>
    </>
  );
};

export default inject('data')(withRouter(ReviewTransaction));